import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED = [
  "What is TDSR?",
  "Which banks do you work with?",
  "How do I get started?",
  "Is my data secure?",
];

export default function FinnyChat({ launcherLabel = "Ask Finny" }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hey! I'm Finny — your AI pre-qualification companion for the Caribbean. Ask me anything about loans, TDSR, banks, or how Financify works.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/finny/chat`, {
        message: msg,
        session_id: sessionId,
      });
      setSessionId(res.data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I'm having trouble right now — try again in a moment, or hop into the qualification form.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen(true)}
        data-testid="finny-launcher"
        className={`fixed bottom-6 right-6 z-[60] group ${
          open ? "hidden" : "inline-flex"
        }`}
        aria-label="Open Finny chat"
      >
        <span className="absolute inset-0 rounded-full finny-pulse" />
        <span className="relative flex items-center gap-3 rounded-full pl-3 pr-5 py-3 bg-gradient-to-br from-[#52BFB8] to-[#3E9690] text-[#040914] font-semibold shadow-[0_8px_32px_rgba(82,191,184,0.4)] transition-transform group-hover:scale-105">
          <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center finny-float">
            <Sparkles className="w-5 h-5 text-[#3E9690]" />
          </span>
          <span className="hidden sm:inline">{launcherLabel}</span>
        </span>
      </button>

      {/* Drawer */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] max-h-[80vh] flex flex-col rounded-3xl overflow-hidden shadow-[0_24px_72px_rgba(0,0,0,0.5)] border border-white/10"
          style={{ background: "rgba(11, 19, 36, 0.98)", backdropFilter: "blur(24px)" }}
          data-testid="finny-drawer"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-gradient-to-r from-[#52BFB8]/15 to-transparent">
            <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-[#52BFB8] to-[#3E9690] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#0B1324]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-white">Finny</p>
              <p className="text-xs text-[#9BA8C4]">AI pre-qualification companion</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-[#9BA8C4] hover:text-white"
              data-testid="finny-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[280px]">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#52BFB8] text-[#040914] font-medium rounded-br-sm"
                      : "bg-white/5 text-white border border-white/10 rounded-bl-sm"
                  }`}
                >
                  {m.content.split("\n").map((line, idx) => (
                    <p key={idx} className={idx > 0 ? "mt-1.5" : ""}>
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/5 rounded-2xl px-4 py-2.5 border border-white/10">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#52BFB8] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 rounded-full bg-[#52BFB8] animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 rounded-full bg-[#52BFB8] animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Suggested chips */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs text-[#52BFB8] border border-[#52BFB8]/40 rounded-full px-3 py-1.5 hover:bg-[#52BFB8]/10 transition"
                  data-testid={`finny-suggest-${s.slice(0, 8)}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2 p-3 border-t border-white/10"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Finny anything..."
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-[#667699] focus:outline-none focus:border-[#52BFB8]"
              data-testid="finny-input"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#52BFB8] text-[#040914] flex items-center justify-center disabled:opacity-50 hover:bg-white transition"
              data-testid="finny-send"
              aria-label="Send"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
